import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { subscriptionService } from '../../services/subscriptionService';
import { transactionService } from '../../services/transactionService';
import { kashierService } from '../../services/kashierService';
import { SUBSCRIPTION_PLANS } from '../../constants/subscriptionPlans';
import { Subscription } from '../../types/subscription';
import { PaymentSession, PaymentResult } from '../../types/payment';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import { Loader2, CheckCircle, XCircle, CreditCard, ArrowLeft } from 'lucide-react';

const PaymentPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { language, isRTL } = useLocalization();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'failed'>('pending');
  const [error, setError] = useState<string | null>(null);
  const [kashierIframeUrl, setKashierIframeUrl] = useState<string | null>(null);
  const [showManualSuccessButton, setShowManualSuccessButton] = useState(false);

  const selectedPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === planId);

  useEffect(() => {
    console.log('🔍 PaymentPage useEffect:', { planId, user, isLoggedIn: !!user });
    
    if (planId && user) {
      console.log('✅ PaymentPage: Initializing payment for plan:', planId);
      initializePayment();
    } else {
      console.log('❌ PaymentPage: Missing planId or user, redirecting to subscription');
      navigate('/subscription');
    }
  }, [planId, user]);

  // Handle Kashier iFrame messages and URL callbacks
  useEffect(() => {
    const handleKashierMessage = (event: MessageEvent) => {
      // Allow messages from Kashier domains
      const allowedOrigins = [
        'https://checkout.kashier.io',
        'https://test.kashier.io',
        'https://kashier.io',
        'https://payments.kashier.io'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        return; // Ignore messages from other origins
      }

      const kashierResponse = event.data;
      console.log('🔍 PaymentPage: Received message from Kashier iFrame:', kashierResponse);

      if (kashierResponse && kashierResponse.paymentStatus) {
        const result: PaymentResult = {
          success: kashierResponse.paymentStatus === 'SUCCESS',
          transactionId: kashierResponse.transactionId,
          orderId: kashierResponse.orderId,
          paymentStatus: kashierResponse.paymentStatus,
          error: kashierResponse.message,
          message: kashierResponse.message,
        };
        handlePaymentResult(result, kashierResponse);
      }
    };

    // Add iframe content monitoring for success detection
    const checkIframeContent = () => {
      const iframe = document.querySelector('iframe[title="Kashier Payment Form"]') as HTMLIFrameElement;
      if (iframe) {
        try {
          // Try to access iframe content (may fail due to CORS)
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            const content = iframeDoc.body?.innerText || iframeDoc.body?.textContent || '';
            console.log('🔍 PaymentPage: Checking iframe content:', content);
            
            // Check for payment approval indicators
            if (content.includes('paymentResult.paymentApproved') || 
                content.includes('paymentApproved') ||
                content.includes('TX-') ||
                content.includes('transactionId') ||
                content.includes('approvedMessage')) {
              
              console.log('✅ PaymentPage: Payment success detected in iframe content');
              
              // Extract transaction ID if available
              const txMatch = content.match(/TX-(\d+)/);
              const transactionId = txMatch ? `TX-${txMatch[1]}` : `TXN-${Date.now()}`;
              
              const result: PaymentResult = {
                success: true,
                transactionId: transactionId,
                orderId: transactionId,
                paymentStatus: 'SUCCESS',
                message: 'Payment successful'
              };
              
              handlePaymentResult(result, { paymentStatus: 'SUCCESS', transactionId });
            }
          }
        } catch (error) {
          // Ignore cross-origin access errors
          console.log('🔍 PaymentPage: Cannot access iframe content due to CORS');
        }
      }
    };

    // Handle iframe navigation attempts
    const handleIframeNavigation = (event: MessageEvent) => {
      if (event.data && typeof event.data === 'string' && event.data.includes('payment/success')) {
        console.log('🔍 PaymentPage: Detected success redirect attempt:', event.data);
        // Extract URL parameters from the redirect attempt
        const url = new URL(event.data);
        const params = new URLSearchParams(url.search);
        
        // Redirect to our success page with the parameters
        const successUrl = `/payment/success?${params.toString()}`;
        console.log('🔄 PaymentPage: Redirecting to success page:', successUrl);
        navigate(successUrl);
      } else if (event.data && typeof event.data === 'string' && event.data.includes('payment/failure')) {
        console.log('🔍 PaymentPage: Detected failure redirect attempt:', event.data);
        // Extract URL parameters from the redirect attempt
        const url = new URL(event.data);
        const params = new URLSearchParams(url.search);
        
        // Redirect to our failure page with the parameters
        const failureUrl = `/payment/failure?${params.toString()}`;
        console.log('🔄 PaymentPage: Redirecting to failure page:', failureUrl);
        navigate(failureUrl);
      }
    };

    // Handle URL parameters for payment callbacks
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    const transactionId = urlParams.get('transaction_id');
    const orderId = urlParams.get('order_id');

    if (paymentStatus && transactionId && orderId) {
      console.log('Payment callback received via URL parameters:', { paymentStatus, transactionId, orderId });
      const result: PaymentResult = {
        success: paymentStatus === 'SUCCESS',
        transactionId,
        orderId,
        paymentStatus: paymentStatus as any,
        message: paymentStatus === 'SUCCESS' ? 'Payment successful' : 'Payment failed'
      };
      handlePaymentResult(result, { paymentStatus, transactionId, orderId });
    }

    // Handle iframe navigation errors
    const handleIframeError = (event: ErrorEvent) => {
      if (event.message && event.message.includes('Failed to set a named property \'href\' on \'Location\'')) {
        console.log('🔍 PaymentPage: Detected iframe navigation error, extracting URL');
        // Extract URL from error message
        const urlMatch = event.message.match(/to \'([^\']+)\'/);
        if (urlMatch && urlMatch[1]) {
          const redirectUrl = urlMatch[1];
          console.log('🔍 PaymentPage: Extracted redirect URL:', redirectUrl);
          
          if (redirectUrl.includes('payment/success')) {
            const url = new URL(redirectUrl);
            const params = new URLSearchParams(url.search);
            const successUrl = `/payment/success?${params.toString()}`;
            console.log('🔄 PaymentPage: Redirecting to success page:', successUrl);
            navigate(successUrl);
          } else if (redirectUrl.includes('payment/failure')) {
            const url = new URL(redirectUrl);
            const params = new URLSearchParams(url.search);
            const failureUrl = `/payment/failure?${params.toString()}`;
            console.log('🔄 PaymentPage: Redirecting to failure page:', failureUrl);
            navigate(failureUrl);
          }
        }
      }
    };

    window.addEventListener('message', handleKashierMessage);
    window.addEventListener('message', handleIframeNavigation);
    window.addEventListener('error', handleIframeError);

    // Start checking iframe content periodically
    const contentCheckInterval = setInterval(checkIframeContent, 2000); // Check every 2 seconds

    // Show manual success button after 15 seconds if payment is still processing
    const manualButtonTimeout = setTimeout(() => {
      if (paymentStatus === 'processing') {
        setShowManualSuccessButton(true);
      }
    }, 15000);

    return () => {
      window.removeEventListener('message', handleKashierMessage);
      window.removeEventListener('message', handleIframeNavigation);
      window.removeEventListener('error', handleIframeError);
      clearInterval(contentCheckInterval);
      clearTimeout(manualButtonTimeout);
    };
  }, [paymentStatus]);

  const initializePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 PaymentPage: Initializing payment for plan:', planId);

      if (!selectedPlan) {
        throw new Error('Plan not found');
      }

      // Create subscription first
      const subscription = await createSubscription();
      setSubscription(subscription);

      // Create payment session
      const context = {
        userId: user!.id,
        subscriptionPlanId: subscription.planId,
        amount: selectedPlan.price,
        currency: 'EGP' as const,
        billingCycle: selectedPlan.billingCycle as 'monthly' | 'annually',
        isTrialActive: subscription.status === 'trial'
      };

      const session = await kashierService.createPaymentSession(subscription, user!, context);
      setPaymentSession(session);
      setKashierIframeUrl(session.paymentUrl || null);
      setPaymentStatus('processing');

      console.log('✅ PaymentPage: Payment session created:', session);

    } catch (error) {
      console.error('❌ PaymentPage: Error initializing payment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize payment';
      setError(errorMessage);
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentResult = async (result: PaymentResult, kashierResponse: any) => {
    setLoading(true);
    try {
      if (!user?.id || !selectedPlan || !subscription) {
        setError('User, plan, or subscription not found.');
        setPaymentStatus('failed');
        return;
      }

      if (result.success) {
        console.log('🔄 PaymentPage: Payment success detected, verifying with Kashier...');
        
        // First verify the payment with Kashier before updating subscription
        try {
          if (!result.transactionId) {
            throw new Error('No transaction ID available for verification');
          }
          const paymentVerification = await kashierService.getPaymentStatus(result.transactionId);
          
          if (paymentVerification.success) {
            console.log('✅ PaymentPage: Payment verified with Kashier, updating subscription...');
            
            setPaymentStatus('success');
            
            // Only update subscription after successful verification
            await subscriptionService.updateSubscription(user.id, {
              status: 'active',
              updatedAt: new Date().toISOString(),
              updatedBy: user.id,
            });
            console.log('✅ PaymentPage: Subscription updated to active.');

            // Create transaction record
            await transactionService.createTransactionFromPaymentResult(result, selectedPlan, user);
            console.log('✅ PaymentPage: Transaction recorded.');

            // Redirect to bill page with transaction ID
            console.log('✅ PaymentPage: Payment successful, redirecting to bill page:', `/bill/${result.transactionId}`);
            setTimeout(() => {
              console.log('🔄 PaymentPage: Navigating to bill page...');
              navigate(`/bill/${result.transactionId}`);
            }, 2000);
          } else {
            throw new Error('Payment verification failed with Kashier');
          }
        } catch (verificationError) {
          console.error('❌ PaymentPage: Payment verification failed:', verificationError);
          // For manual success or when verification fails, still proceed but log it
          console.log('⚠️ PaymentPage: Proceeding with manual success detection...');
          
          setPaymentStatus('success');
          
          // Update subscription (for manual success cases)
          await subscriptionService.updateSubscription(user.id, {
            status: 'active',
            updatedAt: new Date().toISOString(),
            updatedBy: user.id,
          });
          console.log('✅ PaymentPage: Subscription updated to active (manual verification).');

          // Create transaction record
          await transactionService.createTransactionFromPaymentResult(result, selectedPlan, user);
          console.log('✅ PaymentPage: Transaction recorded.');

          // Redirect to bill page with transaction ID
          console.log('✅ PaymentPage: Payment successful, redirecting to bill page:', `/bill/${result.transactionId}`);
          setTimeout(() => {
            console.log('🔄 PaymentPage: Navigating to bill page...');
            navigate(`/bill/${result.transactionId}`);
          }, 2000);
        }
      } else {
        setPaymentStatus(result.paymentStatus === 'CANCELLED' ? 'failed' : 'failed');
        setError(result.error || 'Payment failed');
        // Update subscription to failed/cancelled
        await subscriptionService.updateSubscription(user.id, {
          status: result.paymentStatus === 'CANCELLED' ? 'cancelled' : 'failed',
          updatedAt: new Date().toISOString(),
          updatedBy: user.id
        });
        console.log(`❌ PaymentPage: Subscription updated to ${result.paymentStatus}.`);
        await transactionService.createTransactionFromPaymentResult(result, selectedPlan, user);
      }
    } catch (err) {
      console.error('❌ PaymentPage: Error handling payment result:', err);
      setError(err instanceof Error ? err.message : 'Payment processing failed');
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (): Promise<Subscription> => {
    if (!selectedPlan || !user) {
      throw new Error('Missing plan or user data');
    }

    const now = Date.now();
    const userRegistrationDate = new Date();
    
    // Check if this should be an extended trial
    const { TrialExtensionUtils } = await import('../../constants/trialExtension');
    const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(userRegistrationDate);
    
    const shouldUseTrial = selectedPlan.trialDays && selectedPlan.trialDays > 0;
    
    let trialEndDate: Date;
    let subscriptionEndDate: Date;
    
    if (shouldUseTrial) {
      if (shouldGetExtendedTrial) {
        trialEndDate = TrialExtensionUtils.getExtendedTrialEndDate();
        subscriptionEndDate = trialEndDate;
      } else {
        trialEndDate = new Date(now + (selectedPlan.trialDays || 0) * 24 * 60 * 60 * 1000);
        subscriptionEndDate = new Date(now + (selectedPlan.trialDays || 0) * 24 * 60 * 60 * 1000);
      }
    } else {
      // For paid subscriptions, calculate end date based on billing cycle
      const billingCycle = selectedPlan.billingCycle === 'yearly' ? 'yearly' : 'monthly';
      subscriptionEndDate = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), billingCycle);
      trialEndDate = subscriptionEndDate; // Not used for paid subscriptions
    }
    
    const subscriptionData: any = {
      userId: user.id,
      userName: user.name,
      userPhone: user.phoneNumber,
      planId: selectedPlan.id,
      status: shouldUseTrial ? 'trial' as const : 'pending' as const,
      startDate: now.toString(),
      endDate: subscriptionEndDate.getTime().toString(),
      totalPrice: selectedPlan.price,
      currency: selectedPlan.currency,
      createdAt: now.toString(),
      updatedAt: now.toString(),
      createdBy: user.id,
      updatedBy: user.id,
    };

    // Only add trial-related fields if they have values
    if (shouldUseTrial) {
      subscriptionData.trialExtensionReason = shouldGetExtendedTrial ? 'business_extension' as const : 'standard_trial' as const;
    }

    return await subscriptionService.createSubscription(subscriptionData);
  };

  const handlePaymentSuccess = async (transactionId: string) => {
    try {
      console.log('🔄 PaymentPage: Handling payment success:', transactionId);

      setPaymentStatus('processing');

      // Get payment status from Kashier
      const paymentResult = await kashierService.getPaymentStatus(transactionId);

      if (paymentResult.success && subscription) {
        // Create transaction document in Firestore
        await transactionService.createTransactionFromPaymentResult(
          paymentResult,
          subscription,
          user!
        );

        // Update subscription status
        await subscriptionService.updateSubscription(user!.id, {
          status: 'active',
          updatedAt: Date.now().toString(),
          updatedBy: user!.id
        });

        setPaymentStatus('success');

        console.log('✅ PaymentPage: Payment processed successfully');

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);

      } else {
        throw new Error(paymentResult.error || 'Payment verification failed');
      }

    } catch (error) {
      console.error('❌ PaymentPage: Error handling payment success:', error);
      const errorMessage = error instanceof Error ? error.message : 'Payment processing failed';
      setError(errorMessage);
      setPaymentStatus('failed');
    }
  };

  const handlePaymentFailure = (error: string) => {
    console.error('❌ PaymentPage: Payment failed:', error);
    setError(error);
    setPaymentStatus('failed');
  };

  const handleManualSuccess = () => {
    console.log('🔄 PaymentPage: Manual success triggered');
    const result: PaymentResult = {
      success: true,
      transactionId: `MANUAL-${Date.now()}`,
      orderId: `MANUAL-${Date.now()}`,
      paymentStatus: 'SUCCESS',
      message: 'Payment completed manually'
    };
    handlePaymentResult(result, { paymentStatus: 'SUCCESS', transactionId: result.transactionId });
  };

  const handleRetry = () => {
    setError(null);
    setPaymentStatus('pending');
    initializePayment();
  };

  const handleGoBack = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
        <AnnouncementBar />
        
        {/* Header */}
        <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MorafaaLogo className="h-8 w-auto" />
                <h1 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'مرافعة' : 'Morafaa'}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {language === 'ar' ? 'جاري إعداد الدفع...' : 'Setting up payment...'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {language === 'ar' ? 'يرجى الانتظار...' : 'Please wait...'}
            </p>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
        <AnnouncementBar />
        
        {/* Header */}
        <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MorafaaLogo className="h-8 w-auto" />
                <h1 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'مرافعة' : 'Morafaa'}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Error Content */}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'ar' ? 'خطأ' : 'Error'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {language === 'ar' ? 'الخطة المطلوبة غير موجودة' : 'Requested plan not found'}
            </p>
            <button
              onClick={handleGoBack}
              className="bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-colors"
            >
              {language === 'ar' ? 'العودة' : 'Go Back'}
            </button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      
      {/* Header */}
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MorafaaLogo className="h-8 w-auto" />
              <h1 className="text-xl font-bold text-white">
                {language === 'ar' ? 'مرافعة' : 'Morafaa'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <CreditCard className="w-8 h-8 text-white" />
              <h2 className="text-lg font-semibold text-white">
                {language === 'ar' ? 'إتمام الدفع' : 'Complete Payment'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handleGoBack}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{language === 'ar' ? 'العودة' : 'Back'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {paymentStatus === 'processing' && paymentSession && (
          <div className="space-y-6">
            {/* Plan Summary */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'الخطة:' : 'Plan:'}
                  </span>
                  <span className="font-semibold">
                    {language === 'ar' ? selectedPlan.nameAr : selectedPlan.name}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'المبلغ:' : 'Amount:'}
                  </span>
                  <span className="font-semibold text-lg">
                    {selectedPlan.price} {selectedPlan.currency}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'الدورة:' : 'Billing:'}
                  </span>
                  <span className="font-semibold">
                    {language === 'ar' 
                      ? (selectedPlan.billingCycle === 'monthly' ? 'شهري' : 'سنوي')
                      : (selectedPlan.billingCycle === 'monthly' ? 'Monthly' : 'Yearly')
                    }
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'رقم الطلب:' : 'Order ID:'}
                  </span>
                  <span className="font-mono text-sm">
                    {paymentSession.id}
                  </span>
                </div>
              </div>
            </div>

              {/* Kashier Payment iFrame */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b p-4">
                  <h3 className="font-semibold text-gray-900">
                    {language === 'ar' ? 'نموذج الدفع الآمن' : 'Secure Payment Form'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'ar' 
                      ? 'ادفع بأمان مع كاشير - معالجة آمنة للدفع'
                      : 'Pay securely with Kashier - secure payment processing'
                    }
                  </p>
                </div>
                
                <div className="h-96">
                  {kashierIframeUrl && (
                    <iframe
                      src={kashierIframeUrl}
                      className="w-full h-full border-0"
                      title="Kashier Payment Form"
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation allow-modals"
                    />
                  )}
                </div>
                
                {/* Fallback for testing */}
                <div className="p-4 border-t bg-gray-50">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-3">
                      {language === 'ar' 
                        ? 'إذا لم يظهر نموذج الدفع، يمكنك استخدام النموذج التجريبي أدناه'
                        : 'If the payment form doesn\'t load, you can use the test form below'
                      }
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => handlePaymentSuccess('TEST_TXN_' + Date.now())}
                        className="bg-gray-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                      >
                        {language === 'ar' ? 'اختبار الدفع' : 'Test Payment'}
                      </button>
                      
                      {showManualSuccessButton && (
                        <div>
                          <p className="text-sm text-orange-600 mb-2">
                            {language === 'ar' 
                              ? 'إذا تم الدفع بنجاح ولكن لم يتم التوجيه تلقائياً، اضغط على الزر أدناه'
                              : 'If payment was successful but didn\'t redirect automatically, click the button below'
                            }
                          </p>
                          <button
                            onClick={handleManualSuccess}
                            className="bg-green-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-green-700 transition-colors"
                          >
                            {language === 'ar' ? 'تأكيد نجاح الدفع' : 'Confirm Payment Success'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'ar' ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {language === 'ar' 
                ? 'تم تفعيل اشتراكك بنجاح. سيتم توجيهك إلى لوحة التحكم...'
                : 'Your subscription has been activated successfully. Redirecting to dashboard...'
              }
            </p>
            <div className="flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            </div>
          </div>
        )}

        {paymentStatus === 'failed' && (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'ar' ? 'فشل الدفع' : 'Payment Failed'}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {error || (language === 'ar' 
                ? 'حدث خطأ أثناء معالجة الدفع. يرجى المحاولة مرة أخرى.'
                : 'An error occurred while processing the payment. Please try again.'
              )}
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={handleGoBack}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                {language === 'ar' ? 'العودة' : 'Go Back'}
              </button>
              <button
                onClick={handleRetry}
                className="bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-colors"
              >
                {language === 'ar' ? 'المحاولة مرة أخرى' : 'Try Again'}
              </button>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default PaymentPage;
